/**
 * Hook für Anlagen-Daten (Generation Units)
 * Lädt Anlagen vom Backend und stellt CRUD-Operationen bereit
 */
import { useState, useCallback, useEffect } from 'react';
import { generationUnitsApi } from '../../../../services/api/generationUnits';
import { GenerationUnit, GenerationUnitCreate } from '../../../../types';

interface UseGenerationUnitsDataProps {
  projectId: string | undefined;
}

export function useGenerationUnitsData({ projectId }: UseGenerationUnitsDataProps) {
  const [units, setUnits] = useState<GenerationUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUnits = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await generationUnitsApi.getProjectGenerationUnits(projectId);
      setUnits(data ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fehler beim Laden der Anlagen';
      setError(msg);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const createUnits = useCallback(
    async (items: GenerationUnitCreate[]): Promise<GenerationUnit[]> => {
      if (!projectId) throw new Error('Keine Projekt-ID');
      const created = await generationUnitsApi.createGenerationUnits(projectId, items);
      setUnits((prev) => [...prev, ...created]);
      return created;
    },
    [projectId]
  );

  const updateUnit = useCallback(
    async (unitId: string, data: Partial<GenerationUnitCreate>): Promise<GenerationUnit> => {
      if (!projectId) throw new Error('Keine Projekt-ID');
      const updated = await generationUnitsApi.updateGenerationUnit(projectId, unitId, data);
      setUnits((prev) =>
        prev.map((u) => (u.id === unitId ? updated : u))
      );
      return updated;
    },
    [projectId]
  );

  const deleteUnit = useCallback(async (unitId: string): Promise<void> => {
    if (!projectId) throw new Error('Keine Projekt-ID');
    await generationUnitsApi.deleteGenerationUnit(projectId, unitId);
    setUnits((prev) => prev.filter((u) => u.id !== unitId));
  }, [projectId]);

  return {
    units,
    loading,
    error,
    reload: loadUnits,
    createUnits,
    updateUnit,
    deleteUnit,
  };
}
